import { adminCommunicationsService } from '@/services/admin-communications.service';
import type { AdminFilters } from '@/types/admin.types';
import type { IncidentFormData, IncidentUpdateFormData, MaintenanceFormData, AnnouncementFormData, EmailCampaignFormData } from '@/schemas/communication.schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export function useAdminIncidents(filters: AdminFilters = {}) {
  return useQuery({
    queryKey: ['admin-incidents', filters],
    queryFn: () => adminCommunicationsService.getIncidents(filters),
  });
}

export function useCreateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IncidentFormData) => adminCommunicationsService.createIncident(data),
    onSuccess: () => { toast.success('Incident créé'); qc.invalidateQueries({ queryKey: ['admin-incidents'] }); },
    onError: () => toast.error("Erreur lors de la création de l'incident"),
  });
}

export function useUpdateIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: IncidentUpdateFormData }) => adminCommunicationsService.updateIncident(uuid, data),
    onSuccess: () => { toast.success('Incident mis à jour'); qc.invalidateQueries({ queryKey: ['admin-incidents'] }); },
    onError: () => toast.error('Erreur lors de la mise à jour'),
  });
}

export function useResolveIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => adminCommunicationsService.resolveIncident(uuid),
    onSuccess: () => { toast.success('Incident résolu'); qc.invalidateQueries({ queryKey: ['admin-incidents'] }); },
    onError: () => toast.error('Erreur'),
  });
}

export function useAdminMaintenances(filters: AdminFilters = {}) {
  return useQuery({
    queryKey: ['admin-maintenances', filters],
    queryFn: () => adminCommunicationsService.getMaintenances(filters),
  });
}

export function useCreateMaintenance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MaintenanceFormData) => adminCommunicationsService.createMaintenance(data),
    onSuccess: () => { toast.success('Maintenance planifiée'); qc.invalidateQueries({ queryKey: ['admin-maintenances'] }); },
    onError: () => toast.error('Erreur lors de la planification'),
  });
}

export function useAdminAnnouncements(filters: AdminFilters = {}) {
  return useQuery({
    queryKey: ['admin-announcements', filters],
    queryFn: () => adminCommunicationsService.getAnnouncements(filters),
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AnnouncementFormData) => adminCommunicationsService.createAnnouncement(data),
    onSuccess: () => { toast.success('Annonce créée'); qc.invalidateQueries({ queryKey: ['admin-announcements'] }); },
    onError: () => toast.error("Erreur lors de la création de l'annonce"),
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => adminCommunicationsService.deleteAnnouncement(uuid),
    onSuccess: () => { toast.success('Annonce supprimée'); qc.invalidateQueries({ queryKey: ['admin-announcements'] }); },
    onError: () => toast.error('Erreur lors de la suppression'),
  });
}

export function useAdminCampaigns(filters: AdminFilters = {}) {
  return useQuery({
    queryKey: ['admin-campaigns', filters],
    queryFn: () => adminCommunicationsService.getCampaigns(filters),
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: EmailCampaignFormData) => adminCommunicationsService.createCampaign(data),
    onSuccess: () => { toast.success('Campagne créée'); qc.invalidateQueries({ queryKey: ['admin-campaigns'] }); },
    onError: () => toast.error('Erreur lors de la création'),
  });
}

export function useSendCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => adminCommunicationsService.sendCampaign(uuid),
    onSuccess: () => { toast.success('Campagne envoyée'); qc.invalidateQueries({ queryKey: ['admin-campaigns'] }); },
    onError: () => toast.error("Erreur lors de l'envoi"),
  });
}

export function useCommunicationCounts() {
  return useQuery({
    queryKey: ['admin-comm-counts'],
    queryFn: () => adminCommunicationsService.getCounts(),
  });
}
